import { FinanceReportData, FinanceReportSummary } from '../../types';
import { resolveCurrencyCode, resolveEffectiveRate } from '../../utils/currencyUtils';

interface GeneratorDeps {
  invoices: any[] | undefined;
  invoiceHires: any[] | undefined;
  purchases: any[] | undefined;
  bills: any[] | undefined;
  paymentOrders: any[] | undefined;
  clients: any[] | undefined;
  providers: any[] | undefined;
  branches: any[] | undefined;
  currencies: any[] | undefined;
  currentExchangeRate: number;
}

interface GeneratorParams {
  reportType: string;
  selectedModule: string;
  dateFrom: string;
  dateTo: string;
}

function getName(list: any[] | undefined, id: string, fallback: string): string {
  if (!list || !Array.isArray(list)) return fallback;
  const item = list.find((c: any) => (c.id?.toString() === id || c.documentId === id));
  return item?.name || fallback;
}

export function generateReportData(deps: GeneratorDeps, params: GeneratorParams): FinanceReportData[] {
  const { invoices, invoiceHires, purchases, bills, paymentOrders, clients, providers, branches, currencies, currentExchangeRate } = deps;
  const { reportType, selectedModule, dateFrom, dateTo } = params;
  const data: FinanceReportData[] = [];
  const fromDate = dateFrom ? new Date(dateFrom) : new Date('2024-01-01');
  const toDate = dateTo ? new Date(dateTo) : new Date();

  const pushItem = (
    module: string, category: string, description: string, date: string,
    total: number, isUSD: boolean, rate: number, status: string,
    reference: string, clientVessel: string
  ) => {
    data.push({
      module, category, description, date,
      originalAmount: { usd: isUSD ? total : 0, egp: !isUSD ? total : 0 },
      convertedAmount: {
        usd: isUSD ? total : total / currentExchangeRate,
        egp: isUSD ? total * rate : total
      },
      conversionRate: isUSD ? rate : currentExchangeRate,
      conversionDifference: isUSD ? (total * rate) - (total * currentExchangeRate) : 0,
      status, reference, clientVessel
    });
  };

  if (selectedModule === 'all' || selectedModule === 'invoices') {
    (invoices as any[] || []).forEach((invoice: any) => {
      const invoiceDate = new Date(invoice.issueDate);
      if (invoiceDate >= fromDate && invoiceDate <= toDate) {
        const clientId = invoice.clientId || invoice.client?.documentId || invoice.client?.id || '';
        const vesselId = invoice.vesselId || invoice.vessel?.documentId || invoice.vessel?.id || '';
        const currencyCode = resolveCurrencyCode(invoice.currency, currencies as any[]);
        const isUSD = currencyCode !== 'EGP';
        const rate = resolveEffectiveRate(invoice.exchangeRate, invoice.currency, currencies as any[], currentExchangeRate);
        pushItem('Invoices', 'Income', invoice.description || 'Invoice Payment', invoice.issueDate,
          invoice.total || 0, isUSD, rate, invoice.status || 'pending',
          invoice.id?.toString() || invoice.documentId || '',
          `${getName(clients, clientId.toString(), 'Unknown Client')} - ${getName(branches, vesselId.toString(), 'Unknown Vessel')}`);
      }
    });
  }

  if (selectedModule === 'all' || selectedModule === 'invoice-hire') {
    (invoiceHires as any[] || []).forEach((invoice: any) => {
      const hireDate = new Date(invoice.invoiceDate);
      if (hireDate >= fromDate && hireDate <= toDate) {
        const customerId = invoice.customerId || invoice.customer?.documentId || invoice.customer?.id || '';
        const currencyCode = resolveCurrencyCode(invoice.currency, currencies as any[]);
        const isUSD = currencyCode !== 'EGP';
        const rate = resolveEffectiveRate(invoice.exchangeRate, invoice.currency, currencies as any[], currentExchangeRate);
        pushItem('Invoice Hire/ACM', 'Income', `${invoice.type || 'hire'} - Invoice`, invoice.invoiceDate,
          invoice.netTotal || 0, isUSD, rate, invoice.status || 'pending',
          invoice.invoiceNumber || invoice.id?.toString() || '',
          getName(clients, customerId.toString(), 'Unknown Client'));
      }
    });
  }

  if (selectedModule === 'all' || selectedModule === 'purchases') {
    (purchases as any[] || []).forEach((purchase: any) => {
      const purchaseDate = new Date(purchase.orderDate);
      if (purchaseDate >= fromDate && purchaseDate <= toDate) {
        const providerId = purchase.providerId || purchase.provider?.documentId || purchase.provider?.id || '';
        const currencyCode = resolveCurrencyCode(purchase.currency, currencies as any[]);
        const isUSD = currencyCode !== 'EGP';
        const rate = resolveEffectiveRate(purchase.exchangeRate, purchase.currency, currencies as any[], currentExchangeRate);
        pushItem('Purchases', 'Expense', purchase.notes || 'Purchase Order', purchase.orderDate,
          purchase.total || 0, isUSD, rate, purchase.status || 'pending',
          purchase.id?.toString() || purchase.documentId || '',
          getName(providers, providerId.toString(), 'Unknown Provider'));
      }
    });
  }

  if (selectedModule === 'all' || selectedModule === 'bills') {
    (bills as any[] || []).forEach((bill: any) => {
      const billDate = new Date(bill.createdAt);
      if (billDate >= fromDate && billDate <= toDate) {
        const providerId = bill.providerId || bill.provider?.documentId || bill.provider?.id || '';
        const currencyCode = resolveCurrencyCode(bill.currency, currencies as any[]);
        const isUSD = currencyCode !== 'EGP';
        const rate = resolveEffectiveRate(bill.exchangeRate, bill.currency, currencies as any[], currentExchangeRate);
        pushItem('Bills', 'Expense', bill.description || 'Bill', bill.createdAt,
          bill.amount || 0, isUSD, rate, bill.status || 'pending',
          bill.id?.toString() || bill.documentId || '',
          getName(providers, providerId.toString(), 'Unknown Provider'));
      }
    });
  }

  if (selectedModule === 'all' || selectedModule === 'payment-orders') {
    (paymentOrders as any[] || []).forEach((order: any) => {
      const poDate = new Date(order.date);
      if (poDate >= fromDate && poDate <= toDate) {
        const providerId = order.providerId || order.provider?.documentId || order.provider?.id || '';
        const currencyCode = resolveCurrencyCode(order.currency, currencies as any[]);
        const isUSD = currencyCode !== 'EGP';
        const rate = resolveEffectiveRate(order.exchangeRate, order.currency, currencies as any[], currentExchangeRate);
        pushItem('Payment Orders', 'Expense', `Payment Order - ${order.orderNumber || ''}`, order.date,
          order.total || 0, isUSD, rate, order.status || 'pending',
          order.orderNumber || '', getName(providers, providerId.toString(), 'Unknown Provider'));
      }
    });
  }

  if (reportType === 'usd_only') return data.filter(item => item.originalAmount.usd > 0);
  if (reportType === 'conversion_difference') return data.filter(item => item.conversionDifference && Math.abs(item.conversionDifference) > 0);
  return data;
}

export function calculateSummary(data: FinanceReportData[]): FinanceReportSummary {
  const income = data.filter(item => item.category === 'Income');
  const expenses = data.filter(item => item.category === 'Expense');
  const totalIncome = {
    usd: income.reduce((sum, item) => sum + (item.convertedAmount?.usd || 0), 0),
    egp: income.reduce((sum, item) => sum + (item.convertedAmount?.egp || 0), 0)
  };
  const totalExpenses = {
    usd: expenses.reduce((sum, item) => sum + (item.convertedAmount?.usd || 0), 0),
    egp: expenses.reduce((sum, item) => sum + (item.convertedAmount?.egp || 0), 0)
  };
  const conversionGain = data.filter(item => (item.conversionDifference || 0) > 0)
    .reduce((sum, item) => sum + (item.conversionDifference || 0), 0);
  const conversionLoss = data.filter(item => (item.conversionDifference || 0) < 0)
    .reduce((sum, item) => sum + Math.abs(item.conversionDifference || 0), 0);
  const moduleBreakdown: { [key: string]: { usd: number; egp: number } } = {};
  data.forEach(item => {
    if (!moduleBreakdown[item.module]) moduleBreakdown[item.module] = { usd: 0, egp: 0 };
    moduleBreakdown[item.module].usd += item.convertedAmount?.usd || 0;
    moduleBreakdown[item.module].egp += item.convertedAmount?.egp || 0;
  });
  return {
    totalIncome, totalExpenses,
    netProfit: { usd: totalIncome.usd - totalExpenses.usd, egp: totalIncome.egp - totalExpenses.egp },
    conversionDifferences: { gain: conversionGain, loss: conversionLoss, net: conversionGain - conversionLoss },
    moduleBreakdown
  };
}
